import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Template, TemplateTask } from './template';

@Injectable({ providedIn: 'root' })
export class TemplateService {
  private readonly STORAGE_KEY = 'kbm_templates';
  private readonly STORAGE_KEY_TASKS = 'kbm_template_tasks';

  constructor() {
    // Initialize localStorage if empty
    if (!localStorage.getItem(this.STORAGE_KEY)) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify([]));
    }
    if (!localStorage.getItem(this.STORAGE_KEY_TASKS)) {
      localStorage.setItem(this.STORAGE_KEY_TASKS, JSON.stringify([]));
    }
  }

  private getTemplates(): Template[] {
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  private saveTemplates(templates: Template[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(templates));
  }

  private getTemplateTasks(): TemplateTask[] {
    const data = localStorage.getItem(this.STORAGE_KEY_TASKS);
    return data ? JSON.parse(data) : [];
  }

  private saveTemplateTasks(tasks: TemplateTask[]): void {
    localStorage.setItem(this.STORAGE_KEY_TASKS, JSON.stringify(tasks));
  }

  private getNextTemplateId(): number {
    const templates = this.getTemplates();
    if (templates.length === 0) return 1;
    return Math.max(...templates.map(t => t.templateId || 0)) + 1;
  }

  private getNextTemplateTaskId(): number {
    const tasks = this.getTemplateTasks();
    if (tasks.length === 0) return 1;
    return Math.max(...tasks.map(t => t.templateTaskId || 0)) + 1;
  }

  // Template CRUD operations
  getAll(): Observable<Template[]> {
    const templates = this.getTemplates();
    // Load tasks for each template
    const templateTasks = this.getTemplateTasks();
    templates.forEach(template => {
      template.tasks = templateTasks.filter(t => t.templateId === template.templateId);
    });
    return of(templates);
  }

  getById(templateId: number): Observable<Template> {
    const templates = this.getTemplates();
    const template = templates.find(t => t.templateId === templateId);
    if (template) {
      const templateTasks = this.getTemplateTasks();
      template.tasks = templateTasks.filter(t => t.templateId === templateId);
    }
    return of(template);
  }

  add(template: Template): Observable<any> {
    const templates = this.getTemplates();
    template.templateId = this.getNextTemplateId();
    template.dateCreated = new Date();
    template.dateUpdated = new Date();
    template.isActive = template.isActive !== undefined ? template.isActive : true;
    templates.push(template);
    this.saveTemplates(templates);
    return of({ success: true, data: { template }, message: 'Template created successfully' });
  }

  update(template: Template): Observable<any> {
    const templates = this.getTemplates();
    const index = templates.findIndex(t => t.templateId === template.templateId);
    if (index !== -1) {
      template.dateUpdated = new Date();
      templates[index] = template;
      this.saveTemplates(templates);
      return of({ success: true, data: { template }, message: 'Template updated successfully' });
    }
    return of({ success: false, message: 'Template not found' });
  }

  delete(templateId: number): Observable<any> {
    const templates = this.getTemplates();
    const filtered = templates.filter(t => t.templateId !== templateId);
    this.saveTemplates(filtered);
    // Also delete associated tasks
    const tasks = this.getTemplateTasks();
    const filteredTasks = tasks.filter(t => t.templateId !== templateId);
    this.saveTemplateTasks(filteredTasks);
    return of({ success: true, message: 'Template deleted successfully' });
  }

  // Template Task operations
  addTaskToTemplate(templateId: number, task: TemplateTask): Observable<any> {
    const tasks = this.getTemplateTasks();
    task.templateTaskId = this.getNextTemplateTaskId();
    task.templateId = templateId;
    task.isCompleted = false;
    tasks.push(task);
    this.saveTemplateTasks(tasks);
    return of({ success: true, data: { task }, message: 'Task added to template successfully' });
  }

  updateTemplateTask(task: TemplateTask): Observable<any> {
    const tasks = this.getTemplateTasks();
    const index = tasks.findIndex(t => t.templateTaskId === task.templateTaskId);
    if (index !== -1) {
      tasks[index] = task;
      this.saveTemplateTasks(tasks);
      return of({ success: true, data: { task }, message: 'Template task updated successfully' });
    }
    return of({ success: false, message: 'Template task not found' });
  }

  deleteTemplateTask(templateTaskId: number): Observable<any> {
    const tasks = this.getTemplateTasks();
    const filtered = tasks.filter(t => t.templateTaskId !== templateTaskId);
    this.saveTemplateTasks(filtered);
    return of({ success: true, message: 'Template task deleted successfully' });
  }

  getTemplateTasksByTemplateId(templateId: number): Observable<TemplateTask[]> {
    const tasks = this.getTemplateTasks();
    const filtered = tasks.filter(t => t.templateId === templateId);
    return of(filtered);
  }
}

